import { useState, useEffect } from "react";
import { Database, RotateCcw } from "lucide-react";
import {
  sqlConfigurationService,
  fileConfigurationService,
} from "../../../../services/index.js";
import { readArray, pickVal, parseCsv, stringifyArray } from "./utils.js";
import CompanyList from "./CompanyList.jsx";
import SQLSection from "./SQLSection.jsx";
import FileSection from "./FileSection.jsx";
import SQLModal from "./SQLModal.jsx";
import ConfirmDeleteModal from "../companies/ConfirmDeleteModel.jsx"; // Add this import

export default function DataSourcesView({
  companies = [],
  selectedId,
  setSelectedId,
  triggerToast,
  currentUser,
}) {
  const [sqlConfig, setSqlConfig] = useState(null);
  const [fileConfig, setFileConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'sql' or 'file'
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal states
  const [showSqlModal, setShowSqlModal] = useState(false);
  const [editingSql, setEditingSql] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [sqlFormData, setSqlFormData] = useState({
    databaseType: "",
    serverName: "",
    dbUserName: "",
    dbPassword: "",
    portNum: "",
    databaseName: "",
    connectionstring: "",
    schemaName: "",
    tablesSelected: "",
    viewsSelected: "",
    description: "",
    promptConfiguration: "",
    companyID: "",
    flgSave: 1,
    createdBy: currentUser?.userId || "",
    updatedBy: currentUser?.userId || "",
  });

  // Normalize selected company
  const safeCompanies = companies.map((c) => ({
    id: c.companyID ?? c.companyId ?? c.id,
    name: c.companyName ?? "Unknown",
    email: c.officialEmail ?? "",
    hasSql: c.sqlAnalyticsStatus?.toLowerCase() === "success",
    hasFile: c.fileAnalyticsStatus?.toLowerCase() === "success",
    isActive: c.companyStatus === 1,
  }));
  const selectedSafe = safeCompanies.find((x) => x.id === selectedId);

  // ✅ Fetch SQL + File configs on company select
  const handleSelect = async (id) => {
    if (!id) return;
    
    setSelectedId(id);
    setSqlConfig(null);
    setFileConfig([]);
    setLoading(true);
    try {
      const [sqlConfigRes, fileConfigRes] = await Promise.all([
        sqlConfigurationService.getSQLConfigByCompanyId(id).catch(() => null),
        fileConfigurationService.getFileConfigByCompanyId(id).catch(() => null),
      ]);

      const sqlData = sqlConfigRes?.data || null;
      if (sqlData) {
        const tablesSelected = pickVal(sqlData, ["tablesSelected"]) || "";
        const viewsSelected = pickVal(sqlData, ["viewsSelected"]) || "";
        setSqlConfig({
          configId:
            pickVal(sqlData, ["sqlConfigID", "sqlConfigId", "id"]) || null,
          dbType: pickVal(sqlData, ["databaseType", "dbType"]) || "-",
          schemaName: pickVal(sqlData, ["schemaName"]) || "-",
          databaseName: pickVal(sqlData, ["databaseName", "database"]) || "-",
          serverName: pickVal(sqlData, ["serverName", "server"]) || "-",
          dbUserName: pickVal(sqlData, ["dbUserName"]) || "",
          portNum: pickVal(sqlData, ["portNum"]) || "",
          connectionstring: pickVal(sqlData, ["connectionstring"]) || "",
          promptConfiguration: pickVal(sqlData, ["promptConfiguration"]) || "",
          tablesArray: parseCsv(tablesSelected),
          viewsArray: parseCsv(viewsSelected),
          description: pickVal(sqlData, ["description"]) || "",
        });
      }

      const fileRows = readArray(fileConfigRes);
      const normalizedFiles = fileRows
        .map((row) => ({
          fileName:
            pickVal(row, ["fileName", "originalFileName", "name"]) || "",
          filePath: pickVal(row, ["filePath", "path"]) || "",
          configId: Number(
            pickVal(row, ["fileConfigID", "fileConfigId", "configID", "id"]) ||
              0,
          ),
        }))
        .filter((f) => f.fileName || f.filePath);
      setFileConfig(normalizedFiles);
    } catch (err) {
      console.error("Failed to fetch data source config:", err);
      triggerToast?.("Failed to load data sources", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-fetch data when selectedId is set on initial load
  useEffect(() => {
    if (selectedId) {
      handleSelect(selectedId);
    }
  }, []); // Run only once on mount

  // ✅ SQL modal open
  const openSqlModal = (editConfig = null) => {
    if (editConfig) {
      setEditingSql(editConfig);
      setSqlFormData({
        databaseType: editConfig.dbType !== "-" ? editConfig.dbType : "",
        serverName: editConfig.serverName !== "-" ? editConfig.serverName : "",
        dbUserName: editConfig.dbUserName || "",
        dbPassword: "",
        portNum: editConfig.portNum || "",
        databaseName:
          editConfig.databaseName !== "-" ? editConfig.databaseName : "",
        connectionstring: editConfig.connectionstring || "",
        schemaName: editConfig.schemaName !== "-" ? editConfig.schemaName : "",
        tablesSelected: stringifyArray(editConfig.tablesArray),
        viewsSelected: stringifyArray(editConfig.viewsArray),
        description: editConfig.description || "",
        promptConfiguration: editConfig.promptConfiguration || "",
        companyID: selectedId,
        flgSave: 1,
        createdBy: currentUser?.userId || "",
        updatedBy: currentUser?.userId || "",
      });
    } else {
      setEditingSql(null);
      setSqlFormData({
        databaseType: "",
        serverName: "",
        dbUserName: "",
        dbPassword: "",
        portNum: "",
        databaseName: "",
        connectionstring: "",
        schemaName: "",
        tablesSelected: "",
        viewsSelected: "",
        description: "",
        promptConfiguration: "",
        companyID: selectedId,
        flgSave: 1,
        createdBy: currentUser?.userId || "",
        updatedBy: currentUser?.userId || "",
      });
    }
    setShowSqlModal(true);
  };

  // ✅ SQL save / update
  const handleSqlSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        databaseType: sqlFormData.databaseType,
        serverName: sqlFormData.serverName,
        dbUserName: sqlFormData.dbUserName,
        dbPassword: sqlFormData.dbPassword,
        portNum: sqlFormData.portNum,
        databaseName: sqlFormData.databaseName,
        connectionstring: sqlFormData.connectionstring,
        schemaName: sqlFormData.schemaName,
        tablesSelected: sqlFormData.tablesSelected,
        viewsSelected: sqlFormData.viewsSelected,
        description: sqlFormData.description,
        promptConfiguration: sqlFormData.promptConfiguration,
        companyID: parseInt(sqlFormData.companyID),
        flgSave: editingSql ? 2 : 1,
        createdBy: editingSql ? undefined : parseInt(sqlFormData.createdBy),
        updatedBy: editingSql ? parseInt(sqlFormData.updatedBy) : undefined,
      };

      if (editingSql) {
        await sqlConfigurationService.updateSQLConfig(payload);
        triggerToast?.("SQL configuration updated successfully", "success");
      } else {
        await sqlConfigurationService.saveSQLConfig(payload);
        triggerToast?.("SQL configuration added successfully", "success");
      }

      await handleSelect(selectedId);
      setShowSqlModal(false);
    } catch (error) {
      console.error("Error saving SQL config:", error);
      triggerToast?.("Failed to save SQL configuration", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Handle SQL delete with modal
  const handleSqlDeleteClick = (sqlConfigItem) => {
    setItemToDelete({
      id: sqlConfigItem.configId,
      name: `${sqlConfigItem.dbType} - ${sqlConfigItem.databaseName}`
    });
    setDeleteType('sql');
    setShowDeleteModal(true);
  };

  const handleSqlDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      await sqlConfigurationService.deleteSQLConfig({
        configId: itemToDelete.id,
        companyID: selectedId,
        deletedBy: currentUser?.userId || localStorage.getItem("userId"),
      });
      triggerToast?.("SQL configuration deleted successfully", "success");
      await handleSelect(selectedId);
    } catch (error) {
      console.error("Error deleting SQL config:", error);
      triggerToast?.("Failed to delete SQL configuration", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  // ✅ File upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("📁 File selected:", file.name, "| size:", file.size, "bytes");
    setUploading(true);

    try {
      const userId =
        currentUser?.userId || Number(localStorage.getItem("userId")) || 0;

      const formData = new FormData();
      formData.append("files", file);
      formData.append("CompanyID", selectedId);
      formData.append("CompanyName", selectedSafe?.name || "");
      formData.append("CreatedBy", userId);
      formData.append("UpdatedBy", userId);
      formData.append("PromptConfiguration", "");

      const existingConfigId = fileConfig[0]?.configId || 0;
      console.log("📤 existingConfigId:", existingConfigId);

      if (existingConfigId) {
        console.log("✏️ Calling updateFileConfig — id:", existingConfigId);
        await fileConfigurationService.updateFileConfig(
          formData,
          existingConfigId,
        );
      } else {
        console.log("➕ Calling saveFile");
        await fileConfigurationService.saveFile(formData);
      }

      triggerToast?.("File uploaded successfully", "success");
      await handleSelect(selectedId);
    } catch (error) {
      console.error("❌ File upload failed:", error);
      triggerToast?.("Failed to upload file", "error");
    } finally {
      setUploading(false);
    }
  };

  // ✅ File delete with modal
  const handleFileDeleteClick = (file) => {
    setItemToDelete({
      id: file.configId,
      name: file.fileName
    });
    setDeleteType('file');
    setShowDeleteModal(true);
  };

  const handleFileDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      const userId =
        currentUser?.userId || Number(localStorage.getItem("userId")) || 0;

      const deletePayload = {
        fileConfigID: itemToDelete.id,
        fileConfigId: itemToDelete.id,
        configID: itemToDelete.id,
        id: itemToDelete.id,
        companyID: selectedId,
        companyId: selectedId,
        fileName: itemToDelete.name,
        deletedBy: userId,
        updatedBy: userId,
      };

      console.log("📤 deleteFileConfig payload:", deletePayload);
      await fileConfigurationService.deleteFileConfig(deletePayload);

      triggerToast?.("File deleted successfully", "success");
      await handleSelect(selectedId);
    } catch (error) {
      console.error("❌ File delete failed:", error);
      triggerToast?.("Failed to delete file", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  // ✅ Re-index
  const handleReindex = async (file) => {
    try {
      triggerToast?.("Re-indexing initiated", "info");
    } catch (error) {
      console.error("Error re-indexing file:", error);
      triggerToast?.("Failed to re-index file", "error");
    }
  };

  // ✅ SQL re-ingest
  const handleSqlReingest = (table) => {
    triggerToast?.("Re-ingest initiated", "info");
  };

  // ✅ Sync all data
  const handleSyncAll = () => {
    triggerToast?.("Sync initiated for all data sources", "info");
  };

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col md:flex-row overflow-hidden h-[calc(100vh-12rem)] min-h-[600px] animate-in fade-in slide-in-from-bottom-4 duration-500">

        {/* Left — Company List */}
        <CompanyList
          companies={companies}
          selectedId={selectedId}
          onSelect={handleSelect}
        />

        {/* Right — Detail Panel */}
        <div className="flex-1 p-8 sm:p-12 overflow-y-auto hide-scrollbar bg-white dark:bg-slate-800 relative">

          {/* Empty state */}
          {!selectedId && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-4">
                <Database size={36} className="text-slate-300 dark:text-slate-500" />
              </div>
              <p className="text-lg font-bold text-slate-700 dark:text-slate-300 mb-1">
                No tenant selected
              </p>
              <p className="text-sm text-slate-400">
                Select a tenant from the left to view its data sources.
              </p>
            </div>
          )}

          {/* Loading */}
          {selectedId && loading && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-full border-4 border-[#1d57a5]/20 border-t-[#1d57a5] animate-spin mb-4" />
              <p className="text-sm text-slate-400">Loading data sources…</p>
            </div>
          )}

          {/* Detail */}
          {selectedId && !loading && selectedSafe && (
            <div className="max-w-3xl animate-in fade-in slide-in-from-right-4 duration-500 pb-10">

              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 pb-6 border-b border-slate-100 dark:border-slate-700">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-3xl font-bold text-slate-800 dark:text-white">
                      {selectedSafe.name}
                    </h2>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${
                        selectedSafe.isActive
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {selectedSafe.isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    {selectedSafe.email}
                  </p>
                </div>
                <button
                  onClick={handleSyncAll}
                  className="px-5 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 shadow-sm"
                >
                  <RotateCcw size={16} /> Sync All Data
                </button>
              </div>

              <div className="space-y-12">
                <SQLSection
                  hasSql={selectedSafe.hasSql}
                  sqlConfig={sqlConfig}
                  onAdd={() => openSqlModal(null)}
                  onEdit={() => openSqlModal(sqlConfig)}
                  onDelete={handleSqlDeleteClick}
                  onReingest={handleSqlReingest}
                />

                <FileSection
                  hasFile={selectedSafe.hasFile}
                  files={fileConfig}
                  uploading={uploading}
                  onUpload={handleFileUpload}
                  onReindex={handleReindex}
                  onDelete={handleFileDeleteClick}
                />
              </div>
            </div>
          )}
        </div>

        {/* SQL Modal */}
        <SQLModal
          isOpen={showSqlModal}
          onClose={() => setShowSqlModal(false)}
          onSubmit={handleSqlSubmit}
          editingSql={editingSql}
          sqlFormData={sqlFormData}
          setSqlFormData={setSqlFormData}
          submitting={submitting}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
          setDeleteType(null);
        }}
        onConfirm={deleteType === 'sql' ? handleSqlDeleteConfirm : handleFileDeleteConfirm}
        itemName={itemToDelete?.name || "this item"}
        itemType={deleteType === 'sql' ? "SQL configuration" : "file"}
        isDeleting={isDeleting}
      />
    </>
  );
}